// Import eventBus first to ensure it's available
import { eventBus } from './utils/EventEmitter.js';
import { CanvasManager } from './components/CanvasManager.js';
import { ShaderManager } from './components/ShaderManager.js';
import { EffectsManager } from './components/EffectsManager.js';
import { UIManager } from './components/UIManager.js';
import { PresetManager } from './components/PresetManager.js';
import { ExportManager } from './components/ExportManager.js';
import { AudioManager } from './components/AudioManager.js';

class App {
    constructor() {
        // Initialize managers
        this.canvasManager = new CanvasManager('glitchCanvas');
        this.shaderManager = new ShaderManager(this.canvasManager.getContext());
        this.effectsManager = new EffectsManager(this.shaderManager);
        this.uiManager = new UIManager(this.effectsManager, this.canvasManager);
        this.presetManager = new PresetManager(this.effectsManager, this.canvasManager);
        this.exportManager = new ExportManager(this.canvasManager, this.uiManager);
        this.audioManager = new AudioManager();

        // Set up camera events
        this.setupCameraEvents();
        
        // Set up audio events
        this.setupAudioEvents();

        // Set up animation events
        eventBus.on('animation:render', ({ time }) => {
            this.effectsManager.startTime = time;
            const program = this.effectsManager.applyEffect(
                (time - performance.now()) * 0.001,
                this.canvasManager.getDimensions()
            );
            if (program) {
                this.canvasManager.drawQuad(program);
            }
        });

        // Load initial image
        this.loadInitialImage();

        // Start animation loop
        this.animate();
    }

    setupCameraEvents() {
        eventBus.on('camera:start', async () => {
            try {
                await this.canvasManager.startCamera();
            } catch (error) {
                eventBus.emit('camera:error', error);
            }
        });

        eventBus.on('camera:stop', () => {
            this.canvasManager.stopCamera();
        });
    }

    setupAudioEvents() {
        eventBus.on('audio:start', async () => {
            try {
                await this.audioManager.startAudio();
            } catch (error) {
                eventBus.emit('audio:error', error);
            }
        });

        eventBus.on('audio:stop', () => {
            this.audioManager.stopAudio();
        });

        // Forward audio data to active effect
        eventBus.on('audio:data', (audioData) => {
            const activeEffect = this.effectsManager.getActiveEffect();
            if (activeEffect && typeof activeEffect.handleAudioData === 'function') {
                activeEffect.handleAudioData(audioData);
            }
        });
    }

    async loadInitialImage() {
        // Check for last used URL in localStorage
        const lastUrl = localStorage.getItem('lastUrl');
        
        if (lastUrl) {
            // Emit URL loading event to use existing URL loading logic
            eventBus.emit('image:loadUrl', { url: lastUrl });
        } else {
            // Load default image if no last URL exists
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                eventBus.emit('image:loaded', img);
            };
            img.src = 'trump_snake.jpg';
        }
    }

    animate = () => {
        const time = performance.now() / 1000; // Convert to seconds
        const activeEffect = this.effectsManager.getActiveEffect();
        
        if (activeEffect) {
            const resolution = this.canvasManager.getDimensions();
            this.canvasManager.clear();
            
            // Get and forward current audio data if available
            if (this.audioManager.isAudioActive()) {
                const audioData = this.audioManager.getAudioData();
                if (typeof activeEffect.handleAudioData === 'function') {
                    activeEffect.handleAudioData(audioData);
                }
            }
            
            // Apply effect
            const program = activeEffect.apply(time, resolution);
            this.canvasManager.drawQuad(program);
        }
        
        requestAnimationFrame(this.animate);
    }
}

// Initialize app when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    new App();
});
